<template>
  <main class="container flex-grow mx-auto p-4 sm:p-6">
    <div class="flex items-end justify-between mb-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Menu</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          {{ dining?.date ? formatDate(dining.date) : '(no date from API)' }}
        </p>
        <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Menu shown is as reported and may differ from what is actually offered due to last minute changes or supply issues.
        </p>
      </div>
      <div class="flex items-center gap-2">
        <button
          @click="previousDay"
          class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 transition-colors"
          title="Previous day"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
        </button>
        <UiDatePicker v-model="date" />
        <button
          @click="nextDay"
          class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 transition-colors"
          title="Next day"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center items-center h-24">
      <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-b-4 border-indigo-500 dark:border-indigo-400"></div>
    </div>

    <div v-else-if="error" class="text-gray-600 dark:text-gray-400">{{ error }}</div>

    <div v-else-if="!dining || !dining.has_menu" class="text-gray-600 dark:text-gray-400">
      No lunch menu published for this date.
    </div>

    <div v-else class="space-y-6">
      <div v-for="(section, idx) in sortedSections" :key="idx" class="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
        <h2 class="text-xl font-semibold mb-3 text-gray-900 dark:text-gray-100">{{ section.name }}</h2>
        <ul class="space-y-2">
          <li v-for="(item, iidx) in section.items" :key="iidx" class="flex flex-col sm:flex-row sm:items-baseline sm:justify-between">
            <div class="text-gray-800 dark:text-gray-100">
              <span class="font-medium">{{ item.name }}</span>
              <span v-if="item.description" class="ml-1 text-gray-500 dark:text-gray-400">— {{ item.description }}</span>
              <span v-if="item.allergens?.length" class="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-gray-700 text-amber-800 dark:text-gray-300">
                {{ item.allergens.join(', ') }}
              </span>
            </div>
            <div class="text-sm text-gray-500 dark:text-gray-400 mt-1 sm:mt-0">
              <span v-if="item.serving">{{ item.serving }}</span>
              <span v-if="item.serving && item.calories">&nbsp;•&nbsp;</span>
              <span v-if="item.calories">{{ Math.round(item.calories) }} Cal</span>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </main>
</template>

<script>
import { getDiningForDate } from '@/data/diningClient.js';
import UiDatePicker from '@/components/UiDatePicker.vue';
import { getMenuSectionRank, getSchoolConfig, shouldHideMenuSection, useSchoolContext } from '@/config/schools';

const { activeSchoolId } = useSchoolContext();

export default {
  name: 'MenuPage',
  components: {
    UiDatePicker
  },
  data() {
    const params = new URLSearchParams(window.location.search);
    const fromParam = params.get('testDate');
    const todayIso = new Date().toISOString().slice(0, 10);
    return {
      date: (/^\d{4}-\d{2}-\d{2}$/.test(fromParam || '') ? fromParam : todayIso),
      dining: null,
      loading: false,
      error: ''
    };
  },
  computed: {
    schoolConfig() {
      return getSchoolConfig(activeSchoolId.value);
    },
    sortedSections() {
      if (!this.dining?.sections) return [];

      return this.dining.sections
        .map((section, index) => ({ section, index }))
        .filter(({ section }) => !shouldHideMenuSection(section.name, this.schoolConfig.id))
        .sort((left, right) => {
          const leftRank = getMenuSectionRank(left.section.name, this.schoolConfig.id);
          const rightRank = getMenuSectionRank(right.section.name, this.schoolConfig.id);

          if (leftRank === rightRank) {
            return left.index - right.index;
          }

          return leftRank - rightRank;
        })
        .map(({ section }) => section);
    },
  },
  methods: {
    shiftDateByDays(delta) {
      const [y, m, d] = this.date.split('-').map(Number);
      const currentDate = new Date(Date.UTC(y, m - 1, d));
      currentDate.setUTCDate(currentDate.getUTCDate() + delta);
      const year = currentDate.getUTCFullYear();
      const month = String(currentDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getUTCDate()).padStart(2, '0');
      this.date = `${year}-${month}-${day}`;
    },
    previousDay() {
      this.shiftDateByDays(-1);
    },

    nextDay() {
      this.shiftDateByDays(1);
    },

    async load() {
      this.error = '';
      this.loading = true;

      try {
        this.dining = await getDiningForDate(this.date, this.schoolConfig.id);
      } catch (e) {
        console.error('Error loading menu:', e);
        this.error = e?.message || 'Failed to load menu data';
        this.dining = {
          date: this.date,
          has_menu: false,
          sections: []
        };
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';

      // If it's a pure date like "2025-08-21", format it in UTC to avoid TZ drift
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [y, m, d] = dateString.split('-').map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d)); // construct in UTC
        return dt.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC', // lock to UTC so it won't shift in local timezones
        });
      }

      // Otherwise, fall back to normal Date parsing
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
  },
  watch: {
    date(newDate) {
      if (newDate) {
        this.load();
      }
    },
    'schoolConfig.id'(newSchoolId, oldSchoolId) {
      if (newSchoolId && newSchoolId !== oldSchoolId) {
        this.load();
      }
    },
  },
  mounted() {
    this.load();
  }
};
</script>

<style scoped>
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
